import { useState } from 'react';
import type { ThreadTopicSummary } from '@shared/types/thread-work';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { passTopic, useTeam } from '@/lib/work-handoffs';
import { cn } from '@/lib/utils';

/**
 * « Passer à » (relais, 21/10/2026): pick a colleague, add a word, pass. The
 * server checks that they can open what the task is about.
 */
export const PassToForm = ({ topic, onDone }: {
  topic: Pick<ThreadTopicSummary, 'id' | 'version' | 'title'>;
  onDone: () => void;
}) => {
  const team = useTeam();
  const [to, setTo] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const others = team?.members.filter((member) => member.userId !== team.me) ?? [];
  const chosen = others.find((member) => member.userId === to);

  const submit = async () => {
    if (!to) return;
    setPending(true);
    setError(null);
    try {
      await passTopic({ topicId: topic.id, expectedTopicVersion: topic.version, toUserId: to, note: note || null });
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La tâche n’a pas pu être passée.');
    } finally {
      setPending(false);
    }
  };

  if (team && others.length === 0) return <p className="px-3 py-2 text-sm text-domino-inkSoft">Personne d’autre dans l’équipe.</p>;
  return (
    <form className="space-y-2 p-3" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <div role="group" aria-label="Passer à" className="flex flex-wrap gap-1.5">
        {others.map((member) => (
          <button key={member.userId} type="button" aria-pressed={to === member.userId} onClick={() => setTo(member.userId)}
            className={cn('rounded-full border px-2.5 py-1 text-xs', to === member.userId
              ? 'border-domino-aubergine bg-domino-plumTint text-domino-aubergine'
              : 'border-domino-cardBorder text-domino-ink hover:bg-domino-muted')}>
            {member.name}
          </button>
        ))}
      </div>
      <Textarea aria-label="Un mot" value={note} onChange={(event) => setNote(event.target.value)} rows={2} maxLength={500} className="text-sm" />
      <Button type="submit" size="sm" loading={pending} disabled={!chosen || pending}>{chosen ? `Passer à ${chosen.name}` : 'Passer'}</Button>
      {error && <p role="alert" className="text-xs text-domino-inkSoft">{error}</p>}
    </form>
  );
};
